import { useCallback, useEffect, useState } from "react"
import { getPermitDetail, getPermits, type PermitDetail, type PermitListItem } from "modules/permit/api/permitService"

interface UsePermitReferenceFlowParams {
  tenantId: string
  requesterId?: string | null
  templatePermitCode?: string | null
  onDetailResolved: (detail: PermitDetail) => void
  onDetailFetched?: (detail: PermitDetail) => void
}

interface UsePermitReferenceFlowResult {
  dialog: {
    open: boolean
    onOpenChange: (open: boolean) => void
    search: string
    onSearchChange: (value: string) => void
    items: PermitListItem[]
    isLoading: boolean
    error: string | null
    isApplying: boolean
    onApply: (permitId: string) => void
  }
  actions: {
    openDialog: () => void
  }
}

export function usePermitReferenceFlow({
  tenantId,
  requesterId,
  templatePermitCode,
  onDetailResolved,
  onDetailFetched,
}: UsePermitReferenceFlowParams): UsePermitReferenceFlowResult {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [items, setItems] = useState<PermitListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [pendingReferenceDetail, setPendingReferenceDetail] = useState<PermitDetail | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setIsLoading(true)
      setError(null)

      getPermits({
        tenantId,
        requesterId: requesterId ?? undefined,
        requesterOnly: true,
        searchTerm: search.trim() || undefined,
        pageNumber: 1,
        pageSize: 10,
      })
        .then((result) => {
          if (controller.signal.aborted) return
          setItems(result.items ?? [])
        })
        .catch((fetchError) => {
          if (controller.signal.aborted) return
          console.error("Failed to load reference permits", fetchError)
          setError("โหลดรายการ Permit ไม่สำเร็จ")
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false)
          }
        })
    }, 350)

    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [open, requesterId, search, tenantId])

  const handleApply = useCallback(
    async (permitId: string) => {
      if (!permitId) return

      setIsApplying(true)
      try {
        const detail = await getPermitDetail(tenantId, permitId)
        if (!detail) {
          setError("ไม่พบข้อมูล Permit ที่เลือก")
          return
        }

        onDetailFetched?.(detail)
        setPendingReferenceDetail(detail)

        const templateCode = templatePermitCode?.trim().toLowerCase()
        const detailCode = detail.permitTypeCode?.trim().toLowerCase()
        if (detailCode && templateCode && detailCode === templateCode) {
          onDetailResolved(detail)
          setPendingReferenceDetail(null)
        }

        setOpen(false)
        setSearch("")
      } catch (applyError) {
        console.error("Failed to apply reference permit", applyError)
        setError("คัดลอกข้อมูลไม่สำเร็จ กรุณาลองใหม่")
      } finally {
        setIsApplying(false)
      }
    },
    [onDetailFetched, onDetailResolved, templatePermitCode, tenantId],
  )

  useEffect(() => {
    if (!pendingReferenceDetail) {
      return
    }

    const detail = pendingReferenceDetail
    const detailCode = detail.permitTypeCode?.trim().toLowerCase() ?? ""
    const resolvedTemplateCode = templatePermitCode?.trim().toLowerCase() ?? ""

    if (detailCode && resolvedTemplateCode && detailCode !== resolvedTemplateCode) {
      return
    }
    if (detailCode && !templatePermitCode) {
      return
    }

    const timer = window.setTimeout(() => {
      onDetailResolved(detail)
      setPendingReferenceDetail(null)
    }, 0)

    return () => {
      window.clearTimeout(timer)
    }
  }, [onDetailResolved, pendingReferenceDetail, templatePermitCode])

  return {
    dialog: {
      open,
      onOpenChange: setOpen,
      search,
      onSearchChange: setSearch,
      items,
      isLoading,
      error,
      isApplying,
      onApply: handleApply,
    },
    actions: {
      openDialog: () => setOpen(true),
    },
  }
}
